import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Button, Field, Alert, Spinner } from "@/components/ui";
import { Logo } from "@/components/Logo";
import { ShieldCheck, PartyPopper } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-surface flex flex-col items-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Logo className="h-9" />
        </div>
        {children}
      </div>
    </div>
  );
}

export default function PatientInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | error | done
  const [preview, setPreview] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.peekPatientInvite(token);
        setPreview(p);
        if (p.patient_name) setName(p.patient_name);
        setState("ready");
      } catch (e) {
        setErrMsg(e?.detail || e?.message || "This invite is no longer valid.");
        setState("error");
      }
    })();
  }, [token]);

  const accept = async () => {
    setFormErr(null);
    if (password.length < 8) return setFormErr("Use a password of at least 8 characters.");
    if (password !== confirm) return setFormErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.acceptPatientInvite({ token, password, display_name: name.trim() || undefined });
      await refresh();
      setState("done");
    } catch (e) {
      setFormErr(e?.detail || e?.message || "Couldn't create your account. Ask your family to resend the invite.");
    } finally { setBusy(false); }
  };

  if (state === "loading") return <Shell><Spinner /></Shell>;

  if (state === "error") return (
    <Shell>
      <Card>
        <h1 className="font-display text-2xl text-ink">Invite not available</h1>
        <p className="text-sm text-subtle mt-2">{errMsg}</p>
        <Link to="/login"><Button variant="secondary" className="mt-4" data-testid="pinv-goto-login">Go to sign in</Button></Link>
      </Card>
    </Shell>
  );

  if (state === "done") return (
    <Shell>
      <Card className="text-center" data-testid="pinv-success">
        <div className="mx-auto w-14 h-14 rounded-full bg-mint-tint flex items-center justify-center mb-4"><PartyPopper className="w-7 h-7 text-mint-dark" /></div>
        <h1 className="font-display text-2xl text-ink">You&apos;re all set{name ? `, ${name.split(" ")[0]}` : ""}!</h1>
        <p className="text-sm text-subtle mt-2">Your private health space is ready. Open KinBridge to review your insurance, conditions, and connected wearables.</p>
        <Button className="w-full mt-5" data-testid="pinv-continue" onClick={() => navigate("/app", { replace: true })}>Continue to KinBridge</Button>
      </Card>
    </Shell>
  );

  return (
    <Shell>
      <h1 className="font-display text-3xl text-ink">Hi {preview.patient_name || "there"} 👋</h1>
      <p className="text-sm text-subtle mt-2 mb-6">
        {preview.invited_by || "Your family"}{preview.family_name ? ` (${preview.family_name})` : ""} set up a KinBridge account for you — a private space to review your own insurance, conditions, and connected wearables.
      </p>
      <Card>
        <div className="text-xs font-semibold text-brand uppercase tracking-wide mb-3">Create your password</div>
        <Field label="Sign-in email" data-testid="pinv-email" value={preview.invite_email || ""} readOnly disabled />
        <Field label="Preferred display name" data-testid="pinv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={preview.patient_name || "Your name"} />
        <Field label="New password" type="password" data-testid="pinv-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
        <Field label="Confirm password" type="password" data-testid="pinv-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Retype your password" />
        <Alert testid="pinv-error">{formErr}</Alert>
        <Button className="w-full mt-1" data-testid="pinv-accept" loading={busy} onClick={accept} disabled={!password || !confirm}>Claim my account</Button>
        <p className="flex items-center justify-center gap-1.5 text-xs text-subtle mt-4"><ShieldCheck className="w-3.5 h-3.5" /> Protected under HIPAA-aligned safeguards. Leave the circle anytime.</p>
      </Card>
    </Shell>
  );
}
